import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { EyeOpenIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { attendAlone } from '../../../../../api/affected-staffs/attend-alone'
import { getForeignStaffTravelSummary } from '../../../../../api/affected-staffs/get-foreign-staff-travel-summary'
import { joinGroup } from '../../../../../api/affected-staffs/join-group'
import { Paths } from '../../../../../lib/constants/path'
import useAffectedStaff from '../../../../../lib/hooks/use-affected-staff'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { TravelChoiceEnum } from '../../../../../lib/types/enums/affected-staff/travel-choice.enum'
import { showAxiosError } from '../../../../../lib/utils/functions/misc.functions'

/** Home "Voyage" block of a foreign staff: how they join the group (at the meeting place or by their own means) */
const StaffPersonalSpaceForeignTravelChoiceSection = () => {
  const { t } = useTranslate()
  const { affectedStaff } = useAffectedStaff()
  const { navigate } = useNavigator()
  const queryClient = useQueryClient()

  // Same query key as the travel schedule tab, so both share the cached summary
  const { data: travelSummary } = useQuery({
    queryKey: ['affected-staff', affectedStaff?.id, 'foreign-travel-summary'],
    queryFn: () => {
      if (!affectedStaff?.id) return
      return getForeignStaffTravelSummary(affectedStaff.id)
    },
    enabled: !!affectedStaff?.id
  })

  const refetchAffectedStaff = () =>
    queryClient.invalidateQueries({ queryKey: ['affected-staff'] })

  const joinGroupMutation = useMutation({
    mutationFn: joinGroup,
    onError: showAxiosError,
    onSuccess: async () => await refetchAffectedStaff()
  })

  const attendAloneMutation = useMutation({
    mutationFn: attendAlone,
    onError: showAxiosError,
    onSuccess: async () => await refetchAffectedStaff()
  })

  if (!affectedStaff) return null

  const meetingPlace = valueRender(travelSummary?.group.meetingPlaceLabel ?? undefined)

  return (
    <InnerBox className="rounded-lg p-3 bg-white-hover">
      <div className="flex items-center justify-between w-full mobile:flex-col mobile:items-start mobile:gap-2">
        <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
          {t('staff.personal.space.my.journeys.details.tab.home.trip')}
        </Typography>
        <Button
          variant="ghost"
          icon={EyeOpenIcon}
          className="mobile:w-full"
          label={t('staff.personal.space.my.journeys.details.tab.home.view.trip.data')}
          onClick={() =>
            navigate(
              Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS.replace(
                ':id',
                affectedStaff.id.toString()
              ).replace('*', Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_TRIP_INFORMATION)
            )
          }
        />
      </div>

      <div className="h-2" />
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        {t('staff.personal.space.travel.schedule.foreign.leader.options.title', { meetingPlace })}
      </Typography>
      <div className="h-2" />

      {!affectedStaff.travelChoice ? (
        <div className="flex gap-2 mobile:flex-col tab:flex-col">
          <Button
            label={t('staff.personal.space.travel.schedule.foreign.leader.options.join.group', {
              meetingPlace
            })}
            loading={joinGroupMutation.isPending}
            onClick={() => joinGroupMutation.mutateAsync(affectedStaff.id)}
          />
          <Button
            label={t('staff.personal.space.travel.schedule.foreign.leader.options.attend.alone')}
            loading={attendAloneMutation.isPending}
            onClick={() => attendAloneMutation.mutateAsync(affectedStaff.id)}
          />
        </div>
      ) : (
        <Typography
          variant="caption"
          color={tailwindColors.blue.DEFAULT}
          className='before:content-[""] before:mr-2 before:inline-block before:w-2 before:h-2 before:rounded-full before:bg-primary-light-active'
        >
          {affectedStaff.travelChoice === TravelChoiceEnum.GROUP
            ? t('staff.personal.space.travel.schedule.foreign.leader.options.join.group', {
                meetingPlace
              })
            : t('staff.personal.space.travel.schedule.foreign.leader.options.attend.alone')}
        </Typography>
      )}
    </InnerBox>
  )
}

export default StaffPersonalSpaceForeignTravelChoiceSection
